/**
 * Cometly ad-attribution pixel + browser click-token capture.
 *
 * The pixel loads ONLY on the register and login pages (see
 * `CometlyPixel`): running it across the logged-in app would let invite
 * emails and shared sessions merge unrelated Cometly contacts. Checkout and
 * Stripe webhooks have no pixel at all, so the token captured here is
 * stored (localStorage + `users.comet_token` on the backend) and reused for
 * later conversions.
 *
 * Unlike first-touch signup attribution (`lib/attribution.ts`), the token
 * is LAST-write-wins: a fresh ad click must overwrite the stored token so
 * the next conversion stitches to the newest click.
 */

// Public pixel uid — same uid the marketing site (www.neuroscale.ai) uses,
// so cross-domain sessions stitch. This is a public identifier embedded in
// page HTML by design, not an API secret (the Cometly API key lives in the
// Segment destination settings, never in this repo).
export const COMETLY_PIXEL_SRC =
  "https://e.neuroscale.ai/script.js?uid=d0957d-2251799816000000-20b352-s&domains=www.neuroscale.ai,arbi.neuroscale.ai";

const STORAGE_KEY = "arbi_comet_token";
const MAX_TOKEN_LENGTH = 200;

/** Read the live token from the pixel's `cometToken()` global, if present. */
export function readCometToken(): string | undefined {
  const fn = (globalThis as { cometToken?: () => unknown }).cometToken;
  if (typeof fn !== "function") return undefined;
  try {
    const token = fn();
    if (typeof token !== "string") return undefined;
    const t = token.trim();
    if (!t || t.length > MAX_TOKEN_LENGTH) return undefined;
    return t;
  } catch {
    return undefined;
  }
}

/** Persist the token for pages without the pixel (checkout, OAuth return). */
export function storeCometToken(token: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Storage unavailable (private mode, quota) — attribution is
    // best-effort, never break the page for it.
  }
}

export function getStoredCometToken(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const t = raw.trim();
    if (!t || t.length > MAX_TOKEN_LENGTH) return undefined;
    return t;
  } catch {
    return undefined;
  }
}

/**
 * Freshest available token: the live pixel value when the pixel is on this
 * page (also snapshotted to storage), else the last stored one.
 */
export function currentCometToken(): string | undefined {
  const fresh = readCometToken();
  if (fresh) {
    storeCometToken(fresh);
    return fresh;
  }
  return getStoredCometToken();
}
